import { useNavigate } from "react-router-dom";
import "./GlBankAc.scss";
import { useEffect, useState } from "react";
import { FaSave } from "react-icons/fa";
import { FiSearch } from "react-icons/fi";
import {
    DialogActions,
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper
} from "@mui/material";
import { showToast } from "../../../toastService.js";
import { motion } from "framer-motion";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { customSelectStyles_dark, customSelectStyles_light } from "../../../components/ui/CustomSelect.jsx";
import { useSelector } from "react-redux";
import newRequest from "../../../utils/newRequest";
import { ScaleLoader } from "react-spinners";
import { IoClose, IoAdd, IoSave, IoCheckmarkDoneSharp } from "react-icons/io5";
import CircularButton, { CustomLoadingButton } from "../../../components/ui/CustomButton.jsx";
import { ToggleSwitch } from "../../../components/toogle/ToggleSwitch.jsx";
import ConfirmDialog from "../../../components/confirmDialog/confirmDialog.jsx";
import { MdAdd, MdClose, MdDelete, MdEdit, MdSave } from "react-icons/md";
import { fetchMainLedgers, fetchSubLedgers } from "../../../utils/genericFunctions.js";
import { useTranslation } from "react-i18next";

const GlBankAc = () => {
    const { t } = useTranslation();
    const { currentUser } = useSelector((state) => state.user);
    const { currentTheme } = useSelector((state) => state.theme);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isBankacDialogOpen, setIsBankacDialogOpen] = useState(false);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
    const [confirmDialogAction, setConfirmDialogAction] = useState("None");
    const [confirmationActiontext, setConfirmationActiontext] = useState("");
    const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
    const [notifyDialogTitle, setNotifyDialogTitle] = useState("Confirm");
    const [notifyDialogText, setNotifyDialogText] = useState("Text");
    const [searchTerm, setSearchTerm] = useState("");
    const [bankacPage, setBankacPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedBankAccount, setSelectedBankAccount] = useState(null);

    // account fields
    const [acNo, setAcNo] = useState("");
    const [acBkCode, setAcBkCode] = useState(null);
    const [acBranchCode, setAcBranchCode] = useState(null);
    const [acName, setAcName] = useState("");
    const [acAlias, setAcAlias] = useState("");
    const [acCurCode, setAcCurCode] = useState(null);
    const [acType, setAcType] = useState(null);
    const [acStatus, setAcStatus] = useState({
        value: "Active",
        label: "Active",
    });
    const [acVisible, setAcVisible] = useState("Y");
    const [acReceipt, setAcReceipt] = useState("Y");
    const [acPay, setAcPay] = useState("Y");

    const [acMglCode, setAcMglCode] = useState(null);
    const [acSglCode, setAcSglCode] = useState(null);

    const [currentAction, setCurrentAction] = useState(null);

    const [currencies, setCurrencies] = useState([]);
    const [accountStatuses, setAccountStatuses] = useState([]);
    const [accountTypes, setAccountTypes] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [banks, setBanks] = useState(null);
    const [bankBranches, setBankBranches] = useState(null);

    const fetchCurrencies = async () => {
        try {
            const res = await newRequest.get("/glCurrency/findAll");
            const options = res.data.map((currency) => ({
                value: currency.cur_code,
                label: currency.cur_code,
            }));
            setCurrencies(options);
        } catch (error) {
            console.error("Error fetching entity types:", error);
        }
    };

    const setStatuses = async () => {
        try {
            const options = [{ code: "Active" }, { code: "Closed" }].map((sts) => ({
                value: sts.code,
                label: sts.code,
            }));
            setAccountStatuses(options);
        } catch (error) {
            console.error("Error fetching entity types:", error);
        }
    };

    const fetchAccountTypes = async () => {
        try {
            const res = await newRequest.post("/sysCode/findAll", { sys_type: "Account Type" });
            const options = res.data.map((mode) => ({
                value: mode?.sys_code,
                label: mode?.sys_name,
            }));
            setAccountTypes(options);
        } catch (error) {
            console.error("Error fetching payment modes :", error);
        }
    };


    const fetchBanks = async () => {
        try {
            const res = await newRequest.post("/glBank/findAll");
            const options = res.data?.map((bk) => ({
                branches: bk?.bk_branches,
                value: bk?.bk_code,
                label: bk?.bk_name,
            }));
            // Flatten all branches into one array
            const branchOptions = res.data
                ?.flatMap((bk) => bk?.bk_branches)
                ?.map((br) => ({
                    value: br?.branch_code,
                    label: br?.branch_name,
                }));

            // Set state
            setBanks(options);
            setBankBranches(branchOptions);
        } catch (error) {
            console.error("Error fetching policy claims list:", error);
        }
    };


    useEffect(() => {
        fetchBankAcs();
        fetchCurrencies();
        setStatuses();
        fetchAccountTypes();
        fetchBanks();
    }, []);

    const fetchBankAcs = async () => {
        try {
            setIsLoading(true);
            const res = await newRequest.get("/glBankAc/findAll");
            setBankAccounts(res.data);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const validateEnteredDetails = async () => {
        try {
            if (!acBkCode) {
                setNotifyDialogTitle(t('note'));
                setNotifyDialogText(`Please select bank!`);
                setIsNotifyDialogOpen(true);
                return;
            }

            if (!acBranchCode) {
                setNotifyDialogTitle(t('note'));
                setNotifyDialogText(`Please select bank branch!`);
                setIsNotifyDialogOpen(true);
                return;
            }

            if (!acNo) {
                setNotifyDialogTitle(t('note'));
                setNotifyDialogText(`Please enter Account Number!`);
                setIsNotifyDialogOpen(true);
                return;
            }

            if (!acName) {
                setNotifyDialogTitle(t('note'));
                setNotifyDialogText(`Please enter Account Name!`);
                setIsNotifyDialogOpen(true);
                return;
            }

            if (!acCurCode?.value) {
                setNotifyDialogTitle(t('note'));
                setNotifyDialogText(`Please select account currency!`);
                setIsNotifyDialogOpen(true);
                return;
            }
        } catch (error) {
            console.log(error);
        }
    }
    const handleAccountSubmit = async () => {
        try {
            validateEnteredDetails();
            setConfirmDialogAction("addAccount");
            handleConfirm();
        } catch (error) {
            console.log(error);
        }
    }

    const handleConfirm = async () => {
        try {
            if (confirmDialogAction === "addAccount") {
                const res = await newRequest.post("/glBankAc/add",
                    {
                        ac_bk_code: acBkCode?.value,
                        ac_branch_code: acBranchCode?.value,
                        ac_no: acNo,
                        ac_name: acName,
                        ac_alias: acAlias,
                        ac_cur_code: acCurCode?.value,
                        ac_type: acType?.value,
                        ac_status: acStatus?.value,
                        ac_visible: acVisible,
                        ac_receipt: acReceipt,
                        ac_mgl_code: acMglCode?.value,
                        ac_sgl_code: acSglCode?.value,
                        ac_pay: acPay,
                    });

                if (res.data?.msg === "added") {
                    setBankAccounts((prevReports) => [...prevReports, res.data?.bankac]);
                    setIsBankacDialogOpen(false);
                    setAcBkCode(null);
                    setAcBranchCode(null);
                    setAcNo(null);
                    setAcName(null);
                    setAcAlias(null);
                    setAcCurCode(null);
                    setAcStatus(null);
                    setAcType(null);
                    setAcMglCode(null);
                    setAcSglCode(null);
                    setAcVisible("Y");
                    setAcReceipt("Y");
                    setAcPay("Y");
                    showToast("Added!", "success");
                } else {
                    showToast("Error adding report!", "error");
                }
            } else if (confirmDialogAction === "editBankAccount") {
                validateEnteredDetails();
                const res = await newRequest.put(`/glBankAc/upd/${selectedBankAccount?._id}`,
                    {
                        ac_bk_code: acBkCode?.value,
                        ac_branch_code: acBranchCode?.value,
                        ac_no: acNo,
                        ac_name: acName,
                        ac_alias: acAlias,
                        ac_cur_code: acCurCode?.value,
                        ac_type: acType?.value,
                        ac_status: acStatus?.value,
                        ac_visible: acVisible,
                        ac_receipt: acReceipt,
                        ac_mgl_code: acMglCode?.value,
                        ac_sgl_code: acSglCode?.value,
                        ac_pay: acPay,
                    });

                if (res.data?.msg === "updated") {
                    const updatedAccount = res.data?.bankac;
                    setBankAccounts((prevAccounts) => {
                        return prevAccounts.map((bankac) =>
                            bankac._id === updatedAccount._id ? updatedAccount : bankac
                        );
                    });
                    setIsBankacDialogOpen(false);
                    setAcBkCode(null);
                    setAcBranchCode(null);
                    setAcNo(null);
                    setAcName(null);
                    setAcAlias(null);
                    setAcCurCode(null);
                    setAcStatus(null);
                    setAcType(null);
                    setAcMglCode(null);
                    setAcSglCode(null);
                    setAcVisible("Y");
                    setAcReceipt("Y");
                    setAcPay("Y");
                    showToast("Updated!", "success");
                } else {
                    showToast("Error updating report!", "error");
                }
            }
        } catch (err) {
            console.log(err);
        }
        setIsConfirmDialogOpen(false);
    };


    return (
        <div>
            {isLoading ? (
                <div className="no-content">
                    <ScaleLoader
                        color="var(--primary)"
                        height={50}
                        radius={8}
                        width={4}
                    />
                </div>
            ) : (
                <motion.div
                    className="gl-bankac"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="bankac-navbar">
                        <div className="bankac-navbar-left">
                            <div className="homeSearch" style={{ width: "60%" }}>
                                <FiSearch className="homeSearchIcon" />
                                <input
                                    className="homeSearchInput"
                                    type="text"
                                    placeholder={t('search')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                {searchTerm?.length > 0 ? (
                                    <MdClose
                                        className="homeSearchClear"
                                        onClick={async () => setSearchTerm("")}
                                    />
                                ) : (
                                    <span className="homeSearchClearPlaceholder" />
                                )}
                            </div>
                        </div>
                        <div className="bankac-navbar-right">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#0ea5a4"
                                tooltip={"Add bank account"}
                                endIcon={<MdAdd />}
                                onClick={() => {
                                    try {
                                        setAcCurCode({
                                            value: currentUser.currency_code_xx,
                                            label: currentUser.currency_code_xx
                                        });
                                        setIsBankacDialogOpen(true);
                                        setCurrentAction("addAccount");
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }}
                            >
                                {t('add')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                    <div className="gl-bankac-content">
                        <div className="table-content">
                            <TableContainer component={Paper} sx={{ border: 'none', boxShadow: 'none' }} className="table-container">
                                <Table sx={{ width: "max-content", border: 'none' }}>
                                    <TableHead className="tablehead">
                                        <TableRow className="tablerow">
                                            <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                #
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "200px" }}>
                                                <span>{t('account').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                <span>{t('name').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "320px" }}>
                                                <span>{t('bank').toUpperCase()}</span>
                                            </TableCell>
                                            <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                <span>{t('status').toUpperCase()}</span>
                                            </TableCell>
                                            {["admin", "superadmin"].includes(currentUser.user_type_xx) &&
                                                <TableCell className="table-cell center" style={{ maxWidth: "100px" }}>
                                                    <span>{t('action').toUpperCase()}</span>
                                                </TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody className="tablebody">
                                        {bankAccounts
                                            ?.filter(
                                                (row) =>
                                                    row?.ac_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                    row?.ac_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                    row?.ac_alias?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                    row?.ac_branch_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                                    row?.ac_bk_code?.toLowerCase().includes(searchTerm.toLowerCase())
                                            )
                                            .map((row, index) => (
                                                <TableRow
                                                    key={row?._id}
                                                    className={`${selectedBankAccount?._id === row?._id ? "tablerow current" : "tablerow"}`}
                                                    onClick={() => setSelectedBankAccount(row)}
                                                >
                                                    <TableCell className="table-cell center" style={{ maxWidth: "50px" }}>
                                                        {bankacPage * rowsPerPage + index + 1}
                                                    </TableCell>
                                                    <TableCell className="table-cell" style={{ maxWidth: "200px" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "2px" }}>
                                                            <span className="sub-title">{row?.ac_no}</span>
                                                            <span className="sub-title xrsmall soft">{row?.ac_cur_code}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ width: "auto" }}>
                                                        <div style={{ display: "flex", flexDirection: "column", width: "100%", gap: "2px" }}>
                                                            <span className="sub-title">{row?.ac_name}</span>
                                                            {row?.ac_mgl_code && <span className="sub-title xrsmall">{row?.ac_mgl_code} - {row?.ac_mgl_code_xx}</span>}
                                                            <span className="sub-title xrsmall soft">{row?.bank_branch_name_xx} - {row?.bank_branch_code_xx}</span>
                                                        </div>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "320px" }}>
                                                        <h1 className="sub-title">{row?.bank_name_xx}</h1>
                                                        <h1 className="sub-title soft">{row?.ac_bk_code}</h1>
                                                    </TableCell>
                                                    <TableCell className="table-cell left" style={{ maxWidth: "150px" }}>
                                                        <h3 className={`status ${row?.ac_status}`}>{row?.ac_status}</h3>
                                                    </TableCell>
                                                    {["admin", "superadmin"].includes(currentUser.user_type_xx) &&
                                                        <TableCell className="table-cell" style={{ display: 'flex', flexDirection: "row", alignItems: "center", justifyContent: 'space-around', padding: "3px", maxWidth: "100px", }}>
                                                            <CircularButton
                                                                currentTheme={currentTheme}
                                                                onClick={(e) => {
                                                                    try {
                                                                        e.stopPropagation();
                                                                        setSelectedBankAccount(row);
                                                                        setAcNo(row?.ac_no);
                                                                        setAcName(row?.ac_name);
                                                                        setAcBkCode({
                                                                            value: row?.bank_code_xx,
                                                                            label: row?.bank_name_xx,
                                                                        });
                                                                        setAcBranchCode({
                                                                            value: row?.bank_branch_code_xx,
                                                                            label: row?.bank_branch_name_xx,
                                                                        });
                                                                        setAcAlias(row?.ac_alias);
                                                                        setAcCurCode({
                                                                            value: row?.ac_cur_code,
                                                                            label: row?.ac_cur_code,
                                                                        });
                                                                        setAcType({
                                                                            value: row?.ac_type,
                                                                            label: row?.ac_type_xx,
                                                                        });
                                                                        setAcStatus({
                                                                            value: row?.ac_status,
                                                                            label: row?.ac_status,
                                                                        });
                                                                        setAcVisible(row?.ac_visible);
                                                                        setAcReceipt(row?.ac_receipt);
                                                                        setAcPay(row?.ac_pay);

                                                                        setAcMglCode({
                                                                            value: row?.ac_mgl_code,
                                                                            label: row?.ac_mgl_code_xx,
                                                                        });

                                                                        setAcSglCode({
                                                                            value: row?.ac_sgl_code,
                                                                            label: row?.ac_sgl_code_xx,
                                                                        });

                                                                        setCurrentAction("editBankAccount");
                                                                        setConfirmDialogAction("editBankAccount");
                                                                        setIsBankacDialogOpen(true);
                                                                    } catch (error) {
                                                                        console.log(error);
                                                                    }
                                                                }} icon={<MdEdit color="green" />} width={"35px"} height={"35px"} />
                                                            {/* <CircularButton
                                                                currentTheme={currentTheme}
                                                                onClick={(e) => {
                                                                    try {
                                                                        e.stopPropagation();
                                                                        setSelectedBankAccount(row);
                                                                        setConfirmationActiontext(`Do you want to delete ${row?.ac_no}?`);
                                                                        setConfirmDialogAction("deleteBankAccount");
                                                                        setIsConfirmDialogOpen(true);
                                                                    } catch (error) {
                                                                        console.log(error);
                                                                    }
                                                                }} color="#E53935" icon={<MdDelete/>} width={"35px"} height={"35px"} /> */}
                                                        </TableCell>}
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    </div>
                </motion.div>
            )}
            {isBankacDialogOpen && (
                <motion.div
                    className="overlay-dialog"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { duration: 1 } }}
                    exit={{ opacity: 0, transition: { duration: 1 } }}
                >
                    <div className="overlay-wrapper">
                        <div className="overlay-navbar">
                            <h1 className="main-title">{`${currentAction === "addAccount" ? t('add').toUpperCase() : t('edit').toUpperCase()}`}</h1>
                            <CircularButton
                                currentTheme={currentTheme}
                                onClick={() => {
                                    try {
                                        setAcBkCode(null);
                                        setAcBranchCode(null);
                                        setAcNo(null);
                                        setAcName(null);
                                        setAcAlias(null);
                                        setAcCurCode(null);
                                        setAcStatus(null);
                                        setAcType(null);
                                        setAcMglCode(null);
                                        setAcSglCode(null);
                                        setAcVisible("Y");
                                        setAcReceipt("Y");
                                        setAcPay("Y");
                                        setIsBankacDialogOpen(false);
                                    } catch (error) {
                                        console.log(error);
                                    }
                                }}
                                icon={<IoClose />}
                                width={"35px"}
                                height={"35px"} />
                        </div>
                        <div className="overlay-content">
                            <div className="form-grid-2">
                                <div className="input-item-with-label" >
                                    <label htmlFor="acBkCode"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}>
                                        {t('bank').toUpperCase()}
                                    </label>
                                    <Select
                                        id="acBkCode"
                                        name="acBkCode"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            })
                                        }}
                                        width="320px"
                                        value={acBkCode}
                                        onChange={async (selectedOption) => {
                                            try {
                                                setAcBkCode(selectedOption);
                                                // Find branches for the selected bank
                                                const branchOptions = selectedOption?.branches?.map((branch) => ({
                                                    value: branch.branch_code,
                                                    label: branch.branch_name,
                                                })) || [];
                                                // Set the branches for the selected bank
                                                setAcBranchCode(null); // Clear previously selected branch
                                                setBankBranches(branchOptions); // Update the branch options
                                            } catch (error) {
                                                console.log(error);
                                            }
                                        }}
                                        options={banks}
                                        isSearchable={true}
                                        placeholder={t('bank').toUpperCase()}
                                    />
                                </div>
                                <div className="input-item-with-label" >
                                    <label htmlFor="acBranchCode"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}>
                                        {t('branch').toUpperCase()}
                                    </label>
                                    <Select
                                        id="acBranchCode"
                                        name="acBranchCode"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            })
                                        }}
                                        width="320px"
                                        value={acBranchCode}
                                        onChange={async (selectedOption) => setAcBranchCode(selectedOption)}
                                        options={bankBranches}
                                        isSearchable={true}
                                        placeholder={t('branch').toUpperCase()}
                                    />
                                </div>
                                <div className="input-item-with-label">
                                    <label
                                        htmlFor="acNo"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('no').toUpperCase()}
                                    </label>
                                    <input
                                        id="acNo"
                                        name="acNo"
                                        type="text"
                                        placeholder={t('no').toUpperCase()}
                                        value={acNo}
                                        className="form-input-item"
                                        style={{ width: "320px" }}
                                        onChange={(e) => setAcNo(e.target.value)}
                                    />
                                </div>
                                <div className="input-item-with-label">
                                    <label
                                        htmlFor="acName"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('name').toUpperCase()}
                                    </label>
                                    <input
                                        id="acName"
                                        name="acName"
                                        type="text"
                                        placeholder={t('name').toUpperCase()}
                                        value={acName}
                                        className="form-input-item"
                                        style={{ width: "320px", height: "max-content" }}
                                        onChange={(e) => setAcName(e.target.value)}
                                    />
                                </div>
                                <div className="input-item-with-label">
                                    <label
                                        htmlFor="acAlias"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('alias').toUpperCase()}
                                    </label>
                                    <input
                                        id="acAlias"
                                        name="acAlias"
                                        type="text"
                                        placeholder={t('alias').toUpperCase()}
                                        value={acAlias}
                                        className="form-input-item"
                                        style={{ width: "320px" }}
                                        onChange={(e) => setAcAlias(e.target.value)}
                                    />
                                </div>
                                <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
                                    <div className="input-item-with-label">
                                        <label htmlFor="acCurCode"
                                            className="form-dropdown-label"
                                            style={{ width: "100px" }}
                                        >
                                            {t('currency').toUpperCase()}
                                        </label>
                                        <Select
                                            id="acCurCode"
                                            name="acCurCode"
                                            menuPortalTarget={document.body}
                                            styles={{
                                                ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                                menuPortal: base => ({
                                                    ...base,
                                                    zIndex: 9999,
                                                })
                                            }}
                                            width="100px"
                                            value={acCurCode}
                                            onChange={async (selectedOption) => setAcCurCode(selectedOption)}
                                            options={currencies}
                                            isSearchable={true}
                                            placeholder={t('currency').toUpperCase()}
                                        />
                                    </div>
                                    <div className="input-item-with-label">
                                        <label htmlFor="acStatus"
                                            className="form-dropdown-label"
                                        >
                                            {t('status').toUpperCase()}
                                        </label>
                                        <Select
                                            id="acStatus"
                                            name="acStatus"
                                            menuPortalTarget={document.body}
                                            styles={{
                                                ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                                menuPortal: base => ({
                                                    ...base,
                                                    zIndex: 9999,
                                                })
                                            }}
                                            width="150px"
                                            value={acStatus}
                                            onChange={async (selectedOption) => setAcStatus(selectedOption)}
                                            options={accountStatuses}
                                            isSearchable={true}
                                            placeholder={t('status').toUpperCase()}
                                        />
                                    </div>
                                </div>
                                <div className="input-item-with-label">
                                    <label htmlFor="acType"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('type').toUpperCase()}
                                    </label>
                                    <Select
                                        id="acType"
                                        name="acType"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            })
                                        }}
                                        width="320px"
                                        value={acType}
                                        onChange={async (selectedOption) => setAcType(selectedOption)}
                                        options={accountTypes}
                                        isSearchable={true}
                                        placeholder={t('type')}
                                    />
                                </div>
                            </div>
                            <div className="form-grid-2">
                                <div className="input-item-with-label">
                                    <label htmlFor="ac_mgl_code"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('mainLedger').toUpperCase()}
                                    </label>
                                    <AsyncSelect
                                        id="ac_mgl_code"
                                        name="ac_mgl_code"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            })
                                        }}
                                        width="320px"
                                        value={acMglCode}
                                        onChange={(selectedOption) => setAcMglCode(selectedOption)}
                                        loadOptions={(inputValue, callback) => fetchMainLedgers(inputValue, callback, [])}
                                        isSearchable={true}
                                        placeholder={t('ledgers')}
                                        noOptionsMessage={() => "Type Minimum 2 Letter to Search"} />
                                </div>
                                <div className="input-item-with-label">
                                    <label htmlFor="ac_sgl_code"
                                        className="form-dropdown-label"
                                        style={{ width: "100px" }}
                                    >
                                        {t('subLedger').toUpperCase()}
                                    </label>
                                    <AsyncSelect
                                        id="ac_sgl_code"
                                        name="ac_sgl_code"
                                        menuPortalTarget={document.body}
                                        styles={{
                                            ...(currentTheme === "dark" ? customSelectStyles_dark : customSelectStyles_light),
                                            menuPortal: base => ({
                                                ...base,
                                                zIndex: 9999,
                                            })
                                        }}
                                        width="320px"
                                        value={acSglCode}
                                        onChange={(selectedOption) => setAcSglCode(selectedOption)}
                                        loadOptions={(inputValue, callback) => fetchSubLedgers(inputValue, callback, acMglCode?.value)}
                                        isSearchable={true}
                                        placeholder={t('ledgers')}
                                        noOptionsMessage={() => "Type Minimum 2 Letter to Search"} />
                                </div>
                            </div>
                            <div className="form-grid-2">
                                <div className="input-item-with-label">
                                    <label htmlFor="acReceipt"
                                        className="form-dropdown-label"
                                        style={{ width: "90px" }} />
                                    <ToggleSwitch
                                        id="acReceipt"
                                        value={acReceipt}
                                        onChange={setAcReceipt}
                                        labelPosition="end"
                                        label={t('allowReceiptsIntoAc').toUpperCase()}
                                        labelWidth="max-content"
                                        border="N"
                                        style={{ width: "max-content" }}
                                    />
                                </div>
                                <div className="input-item-with-label">
                                    <label htmlFor="acPay"
                                        className="form-dropdown-label"
                                        style={{ width: "90px" }} />
                                    <ToggleSwitch
                                        id="acPay"
                                        value={acPay}
                                        onChange={setAcPay}
                                        labelPosition="end"
                                        label={t('allowPaymentsFromAc').toUpperCase()}
                                        labelWidth="max-content"
                                        border="N"
                                        style={{ width: "max-content" }}
                                    />
                                </div>
                            </div>
                            <div className="input-item-with-label">
                                <label htmlFor="acVisible"
                                    className="form-dropdown-label"
                                    style={{ width: "90px" }} />
                                <ToggleSwitch
                                    id="acVisible"
                                    value={acVisible}
                                    onChange={setAcVisible}
                                    labelPosition="end"
                                    label={"USERS CAN SEE THIS ACCOUNT"}
                                    labelWidth="max-content"
                                    border="N"
                                    style={{
                                        width: "max-content",
                                    }}
                                />
                            </div>
                        </div>
                        <div className="overlay-actions">
                            <CustomLoadingButton
                                currentTheme={currentTheme}
                                iconPosition="end"
                                borderRadiusType="both"
                                variant="tint"
                                color="#1976D2"
                                endIcon={<IoCheckmarkDoneSharp />}
                                onClick={handleAccountSubmit}>
                                {currentAction === "addAccount" ? t('add') : t('update')}
                            </CustomLoadingButton>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Dialog to notify user about changes being saved */}
            <Dialog open={isNotifyDialogOpen}
                onClose={() => setIsNotifyDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: currentTheme === "dark" ? "#181A20" : "#fff",
                        color: currentTheme === "dark" ? "#fff" : "#000",
                    },
                }}
                slotProps={{
                    backdrop: {
                        sx: {
                            backgroundColor: "rgba(0, 0, 0, 0.7)",
                        },
                    },
                }}>
                <DialogTitle>{notifyDialogTitle}</DialogTitle>
                <DialogContent>
                    <div dangerouslySetInnerHTML={{ __html: notifyDialogText }} />
                </DialogContent>
                <DialogActions>
                    <CustomLoadingButton
                        currentTheme={currentTheme}
                        variant="tint"
                        color="#E040FB"
                        onClick={() => setIsNotifyDialogOpen(false)}>
                        {t('close')}
                    </CustomLoadingButton>
                </DialogActions>
            </Dialog>
            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setIsConfirmDialogOpen(false)}
                onConfirm={handleConfirm}
                dialogTitle="Confirmation"
                dialogContent={<p dangerouslySetInnerHTML={{ __html: confirmationActiontext }} />}
            />
        </div>
    )
}

export default GlBankAc;